import { useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as HistoryIcon } from 'assets/iconButtons/history.svg';
import i18n from 'i18n';
import { FEE_TYPE } from 'components/constants';
import { formatMoney } from 'services/utils';
import { classNames } from 'uikit/utils';
import './OriginalCommission.scss';

const isDefined = (value) => value !== null && value !== undefined;

// Text description of a tariff rule, e.g. "Fixed · 6.00" or "50% · min 2.00 · max 30.00". It is the same for
// the original and the new commissions, so the operator can see why they differ.
export const formatTariffRule = (rule, precision) => {
  if (rule?.feeType === FEE_TYPE.FIXED) {
    return [
      i18n.getMessage(`tariffFeeType.${FEE_TYPE.FIXED}`),
      isDefined(rule.fixedValue) && formatMoney(rule.fixedValue, precision)
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (rule?.feeType === FEE_TYPE.PERCENT) {
    return [
      isDefined(rule.percentValue) ? `${rule.percentValue}%` : i18n.getMessage(`tariffFeeType.${FEE_TYPE.PERCENT}`),
      isDefined(rule.min) &&
        i18n.getMessage('createTransaction.payment.commission.tariffRule.min', {
          value: formatMoney(rule.min, precision)
        }),
      isDefined(rule.max) &&
        i18n.getMessage('createTransaction.payment.commission.tariffRule.max', {
          value: formatMoney(rule.max, precision)
        })
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return null;
};

const getTariffName = (details) =>
  details?.systemTariffKey ||
  (details?.tariffGroupId
    ? i18n.getMessage('createTransaction.payment.commission.original.tariffGroup', { id: details.tariffGroupId })
    : i18n.getMessage('createTransaction.payment.commission.original.customCommission'));

// Commission of the original transaction (e.g. a repeated one) with the tariffs it was made of. It is shown for
// reference only, to compare with the new commissions.
const OriginalCommission = ({ commission, currency, precision }) => {
  const [isTariffsExpanded, setIsTariffsExpanded] = useState(false);

  const appliedTariffs = commission.appliedTariffs || [];
  // Tariffs are in the currency of the original transaction
  const totalCurrency = appliedTariffs.find(({ details }) => details?.currency)?.details.currency || currency;

  return (
    <div className={'original-commission'}>
      <div className={'original-commission-header'}>
        <HistoryIcon className={'original-commission-icon'} />
        <span className={'original-commission-title'}>
          {i18n.getMessage('createTransaction.payment.commission.original.title')}
        </span>
        <span className={'original-commission-total'}>
          {isDefined(commission.total) ? `${formatMoney(commission.total, precision)} ${totalCurrency}` : '—'}
        </span>
      </div>

      {appliedTariffs.length > 0 && (
        <div className={'original-commission-tariffs'}>
          <button
            type={'button'}
            className={'original-commission-toggle'}
            aria-expanded={isTariffsExpanded}
            onClick={() => setIsTariffsExpanded(!isTariffsExpanded)}
          >
            {/* The chevron sits under the icon, so the text is aligned with the title */}
            <span className={'original-commission-toggle-icon'}>
              <span
                className={classNames({ 'original-commission-toggle-chevron': true, expanded: isTariffsExpanded })}
              />
            </span>
            {isTariffsExpanded
              ? i18n.getMessage('createTransaction.payment.commission.original.hideTariffs')
              : i18n.getMessage('createTransaction.payment.commission.original.showTariffs', {
                  count: appliedTariffs.length
                })}
          </button>

          {isTariffsExpanded && (
            <ul className={'original-commission-tariffs-list'}>
              {appliedTariffs.map(({ amount, details }, index) => {
                const rule = formatTariffRule(details, precision);

                return (
                  <li key={index} className={'original-commission-tariff'}>
                    <div className={'original-commission-tariff-info'}>
                      <div className={'original-commission-tariff-name'}>
                        {getTariffName(details)}
                        {details?.highRiskCountries && (
                          <span className={'original-commission-tariff-badge'}>
                            {i18n.getMessage('createTransaction.payment.commission.original.highRisk')}
                          </span>
                        )}
                      </div>
                      {rule && <span className={'original-commission-tariff-rule'}>{rule}</span>}
                    </div>
                    <span className={'original-commission-tariff-amount'}>
                      {isDefined(amount) ? formatMoney(amount, precision) : '—'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const originalCommissionPropType = PropTypes.shape({
  total: PropTypes.number,
  appliedTariffs: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      details: PropTypes.shape({
        tariffGroupId: PropTypes.string,
        systemTariffKey: PropTypes.string,
        feeType: PropTypes.string,
        currency: PropTypes.string,
        highRiskCountries: PropTypes.bool,
        fixedValue: PropTypes.number,
        percentValue: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number
      })
    })
  )
});

OriginalCommission.propTypes = {
  commission: originalCommissionPropType.isRequired,
  currency: PropTypes.string.isRequired,
  precision: PropTypes.number.isRequired
};

export default OriginalCommission;
